




import { useState } from 'react';
import { User, Mail, Smartphone, Lock, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';

const SignupForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    mobile: '',
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [buttonState, setButtonState] = useState('default');
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 10;
    return Math.min(strength, 100);
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: undefined });
    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    else if (formData.username.length < 3) newErrors.username = 'Username must be at least 3 characters';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!emailRegex.test(formData.email)) newErrors.email = 'Enter a valid email';

    const mobileRegex = /^[\d\s\-\+\(\)]+$/;
    const cleanMobile = formData.mobile.replace(/[\s\-\(\)]/g, '');
    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    else if (!mobileRegex.test(formData.mobile) || cleanMobile.length < 10) newErrors.mobile = 'Enter a valid mobile';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 chars';

    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirm your password';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setButtonState('error');
      setTimeout(() => setButtonState('default'), 500);
      return;
    }

    try {
      setButtonState('loading');

      const response = await axios.post('https://api.nfcdesignstudio.com/API/v1/signup',
        {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          mobile: formData.mobile,
        }
      );

      // Axios does NOT use response.ok
      const data = response.data;

      setButtonState('success');

      setTimeout(() => {
        setShowOtp(true);
        setButtonState('default');
      }, 800);

    } catch (error) {
      console.error(error);

      setButtonState('error');

      // Axios error response handling
      if (error.response && error.response.data) {
        alert(error.response.data.error || "Signup failed");
      }

      setTimeout(() => setButtonState('default'), 800);
    }
  };



  const handleVerifyOtp = async () => {
    if (!otp) return alert("Enter OTP");
    try {
      setOtpLoading(true);
      const response = await fetch('https://api.nfcdesignstudio.com/API/v1/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          otp: otp,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error);
        setOtpLoading(false);
        return;
      }

      alert("User verified successfully ✅");
      localStorage.setItem('token', data.token);

      // CRITICAL FIX: Tell the Modal we are done
      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error(error);
      alert("Verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  const getPasswordColor = () => {
    if (passwordStrength < 40) return '#f87171';
    if (passwordStrength < 70) return '#facc15';
    return '#4ade80';
  };

  const getPasswordText = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };



  const inputStyle = (error) => ({ width: '100%', padding: '12px 16px', paddingLeft: '48px', border: `2px solid ${error ? '#f87171' : '#d1d5db'}`, borderRadius: '12px', backgroundColor: '#f3f4f6', color: '#111827', outline: 'none', });
  const buttonStyle = { width: '100%', padding: '12px', borderRadius: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: buttonState === 'loading' || buttonState === 'success' ? 'not-allowed' : 'pointer', background: buttonState === 'success' ? '#4ade80' : buttonState === 'error' ? '#f87171' : 'linear-gradient(to right, #6366f1, #ec4899)', color: 'white', border: 'none', };



  return (
    <>
      {!showOtp ? (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Username</label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', }}><User size={20} /></div>
              <input type="text" value={formData.username} onChange={(e) => handleChange('username', e.target.value)} placeholder="Choose a username" style={inputStyle(errors.username)} />
            </div>
            {errors.username && <div style={{ color: '#f87171', fontSize: '0.875rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', }}><AlertCircle size={16} /> {errors.username}</div>}
          </div>

          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Email</label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', }}><Mail size={20} /></div>
              <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="your@email.com" style={inputStyle(errors.email)} />
            </div>
            {errors.email && <div style={{ color: '#f87171', fontSize: '0.875rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', }}><AlertCircle size={16} /> {errors.email}</div>}
          </div>

          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Mobile</label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', }}><Smartphone size={20} /></div>
              <input type="tel" value={formData.mobile} onChange={(e) => handleChange('mobile', e.target.value)} placeholder="+1 (555) 123-4567" style={inputStyle(errors.mobile)} />
            </div>
            {errors.mobile && <div style={{ color: '#f87171', fontSize: '0.875rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', }}><AlertCircle size={16} /> {errors.mobile}</div>}
          </div>

          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', }}><Lock size={20} /></div>
              <input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => handleChange('password', e.target.value)} placeholder="Create a strong password" style={{ ...inputStyle(errors.password), paddingRight: '18px' }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', }}>
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formData.password && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                  <span>Password strength</span>
                  <span style={{ fontWeight: '600', color: getPasswordColor() }}>{getPasswordText()}</span>
                </div>
                <div style={{ height: '6px', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden', }}>
                  <div style={{ height: '100%', width: `${passwordStrength}%`, backgroundColor: getPasswordColor(), transition: 'width 0.3s', }}></div>
                </div>
              </div>
            )}
            {errors.password && <div style={{ color: '#f87171', fontSize: '0.875rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', }}><AlertCircle size={16} /> {errors.password}</div>}
          </div>

          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Confirm Password</label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', }}><Lock size={20} /></div>
              <input type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} placeholder="Confirm your password" style={{ ...inputStyle(errors.confirmPassword), paddingRight: '18px' }} />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', }}>
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              {formData.confirmPassword && formData.password === formData.confirmPassword && !errors.confirmPassword && (
                <div style={{ position: 'absolute', right: '48px', top: '50%', transform: 'translateY(-50%)', color: '#4ade80' }}>
                  <Check size={20} />
                </div>
              )}
            </div>
            {errors.confirmPassword && <div style={{ color: '#f87171', fontSize: '0.875rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', }}><AlertCircle size={16} /> {errors.confirmPassword}</div>}
          </div>

          {/* Terms */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', paddingTop: '8px' }}>
            <input type="checkbox" required style={{ width: '16px', height: '16px' }} />
            <label style={{ fontSize: '0.875rem', color: '#6b7280', cursor: 'pointer' }}>
              I agree to the{' '}
              <button style={{ color: '#6366f1', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}>
                Terms & Conditions
              </button>{' '}
              and{' '}
              <button style={{ color: '#6366f1', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}>
                Privacy Policy
              </button>
            </label>
          </div>

          {/* Submit */}
          <button type="submit" disabled={buttonState === 'loading' || buttonState === 'success'} style={buttonStyle}>
            {buttonState === 'loading' && 'Creating account...'}
            {buttonState === 'success' && 'Account created!'}
            {(buttonState === 'default' || buttonState === 'error') && 'Create Account'}
          </button>
        </form>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h2 style={{ textAlign: 'center' }}>OTP Verification</h2>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Enter the 6-digit OTP sent to {formData.email}</p>
          <input type="text" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter OTP" maxLength={6} style={{ padding: '14px', fontSize: '18px', textAlign: 'center', letterSpacing: '8px', borderRadius: '12px', border: '2px solid #d1d5db', }} />
          <button type="button" onClick={handleVerifyOtp} disabled={otpLoading} style={{ padding: '12px', borderRadius: '12px', background: 'linear-gradient(to right, #6366f1, #ec4899)', color: 'white', border: 'none', fontWeight: '600', cursor: 'pointer' }}>
            {otpLoading ? "Verifying..." : "Verify OTP"}
          </button>
        </div>
      )}
    </>
  );
};

export default SignupForm;
